import React from 'react';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useAuth } from '../contexts/AuthContext';
import NotFound from '../pages/NotFound';
import { canAccessPermissionsManagement } from '../utils/permissionsAccess';

interface PermissionGuardProps {
  children: React.ReactNode;
  resource?: string;
  action?: 'create' | 'read' | 'update' | 'delete';
  requireAny?: boolean; // If true, requires at least one of the actions; if false, requires the specific action
  roles?: string[]; // Optional: restrict by user role names (e.g., ['admin', 'super_admin'])
}

/**
 * PermissionGuard component that checks if user has permission to access a resource
 * If user doesn't have permission, shows NotFound without changing the URL
 */
function PermissionGuardLoading() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center p-8">
      <div
        className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-primary-600"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  resource,
  action: _action = 'read',
  requireAny: _requireAny = false,
  roles
}) => {
  const { user, hasAnyRole, isSuperAdmin } = useAuth();
  const { canAccessMenuItem, loading: permissionsLoading } = useUserPermissions();

  // GOD roles (super_admin) have access to everything
  if (isSuperAdmin()) {
    return <>{children}</>;
  }

  // If roles are specified, check if user's role is in the allowed list
  if (roles && roles.length > 0) {
    if (!hasAnyRole(roles)) {
      return <NotFound />;
    }

    // If only roles are specified (no resource), grant access
    if (!resource) {
      return <>{children}</>;
    }
  }

  // Resource checks use async /permissions/my — avoid a false NotFound flash while loading
  if (resource && permissionsLoading) {
    return <PermissionGuardLoading />;
  }

  // If resource is specified, check resource permissions
  if (resource) {
    if (resource === 'permissions') {
      const hasPermissionsRead = canAccessMenuItem('permissions');
      if (!canAccessPermissionsManagement(user, hasPermissionsRead)) {
        return <NotFound />;
      }
      return <>{children}</>;
    }

    const hasAccess = canAccessMenuItem(resource);

    if (!hasAccess) {
      return <NotFound />;
    }
  }

  return <>{children}</>;
};

export default PermissionGuard;
